import type { Vec2 } from '@/geometry/vec2';

/**
 * Crease assignments, using the FOLD specification's single-letter codes.
 *
 *  B  boundary of the sheet (never folds)
 *  M  mountain  - folds away from the viewer, negative fold angle
 *  V  valley    - folds toward the viewer, positive fold angle
 *  F  flat      - a real edge that stays flat (fold angle 0)
 *  U  unassigned - drawn but no M/V decision yet
 *  C  cut       - the sheet is severed here; the two sides are disconnected
 *  J  join / auxiliary - internal edge of one flat facet (triangulation edge);
 *     target fold angle 0, hidden by default
 */
export type CreaseAssignment = 'B' | 'M' | 'V' | 'F' | 'U' | 'C' | 'J';

export const CREASE_ASSIGNMENTS: CreaseAssignment[] = ['B', 'M', 'V', 'F', 'U', 'C', 'J'];

export const ASSIGNMENT_LABEL: Record<CreaseAssignment, string> = {
  B: 'Boundary',
  M: 'Mountain',
  V: 'Valley',
  F: 'Flat',
  U: 'Unassigned',
  C: 'Cut',
  J: 'Auxiliary',
};

/**
 * Standard origami crease-pattern colours, for a light background (this is the
 * set used for SVG export and printing).
 */
export const ASSIGNMENT_COLOR: Record<CreaseAssignment, string> = {
  B: '#1c1f26',
  M: '#e5484d',
  V: '#3b82f6',
  F: '#9aa4b2',
  U: '#c9ced6',
  C: '#22c55e',
  J: '#7c4dff',
};

/**
 * Dark-background variant. Only the near-black and near-white assignments move;
 * mountain red and valley blue are the convention and stay put.
 */
export const ASSIGNMENT_COLOR_DARK: Record<CreaseAssignment, string> = {
  ...ASSIGNMENT_COLOR,
  B: '#c3cad6',
  M: '#ff5b60',
  V: '#5b9bff',
  F: '#78818f',
  U: '#8f98a6',
};

export const assignmentColor = (
  assignment: CreaseAssignment,
  theme: 'dark' | 'light',
): string => (theme === 'light' ? ASSIGNMENT_COLOR : ASSIGNMENT_COLOR_DARK)[assignment];

/** Does this assignment carry a hinge in the simulation? */
export const isFoldable = (a: CreaseAssignment): boolean =>
  a === 'M' || a === 'V' || a === 'F' || a === 'U' || a === 'J';

/** Does this assignment sever the sheet? */
export const isCut = (a: CreaseAssignment): boolean => a === 'C';

export interface CreasePatternMeta {
  title?: string;
  author?: string;
  description?: string;
  /** Free-form provenance (e.g. imported filename). */
  source?: string;
  /** Solver mode the pattern was designed for. */
  recommendedSolver?: 'rigid' | 'compliant';
  unit?: string;
}

/**
 * The canonical editable state. Deliberately plain data: no class instances, no
 * Three.js objects, structurally cloneable so it can be posted to a Worker and
 * stored in IndexedDB as-is.
 *
 * Faces are derived (see `topology.ts`); they live here so that importers can
 * supply them and so that consumers do not have to re-derive on every read.
 */
export interface CreasePattern {
  verticesCoords: Vec2[];
  edgesVertices: [number, number][];
  edgesAssignment: CreaseAssignment[];
  /**
   * Target fold angle in radians for each edge, or null to derive it from the
   * assignment (mountain -> -pi, valley -> +pi, everything else -> 0).
   * Sign convention: see src/origami/angles.ts.
   */
  edgesFoldAngle: (number | null)[];
  /** Counter-clockwise vertex loops. Derived, but cached on the pattern. */
  facesVertices: number[][];
  meta: CreasePatternMeta;
}

export interface VertexRef {
  kind: 'vertex';
  index: number;
}
export interface EdgeRef {
  kind: 'edge';
  index: number;
}
export interface FaceRef {
  kind: 'face';
  index: number;
}
export type SelectionRef = VertexRef | EdgeRef | FaceRef;

export const emptyPattern = (): CreasePattern => ({
  verticesCoords: [],
  edgesVertices: [],
  edgesAssignment: [],
  edgesFoldAngle: [],
  facesVertices: [],
  meta: {},
});

export function clonePattern(p: CreasePattern): CreasePattern {
  return {
    verticesCoords: p.verticesCoords.map((v) => ({ x: v.x, y: v.y })),
    edgesVertices: p.edgesVertices.map((e) => [e[0], e[1]] as [number, number]),
    edgesAssignment: p.edgesAssignment.slice(),
    edgesFoldAngle: p.edgesFoldAngle.slice(),
    facesVertices: p.facesVertices.map((f) => f.slice()),
    meta: { ...p.meta },
  };
}
